<script lang="ts" setup>
import { ref, computed, watch } from 'vue';
import { MMText } from '@mm-frontend/mm-web-ui-kit';

const emit = defineEmits(['filled']);

defineProps<{
  error: string;
}>();

const model = defineModel<string>({ default: '' });
const modelLength = computed(() => model.value.length);

function activeDigit(index: number) {
  const lastFilledIndex = modelLength.value - 1;
  if (lastFilledIndex === 4 && index === 4) {
    return true;
  }

  return index === lastFilledIndex + 1;
}

const isFocused = ref(false);

function onFocus() {
  isFocused.value = true;
  moveCursorToEnd();
}

function onBlur() {
  isFocused.value = false;
}

function moveCursorToEnd() {
  const input = document.querySelector('.code-input__input') as HTMLInputElement;
  if (input) {
    input.setSelectionRange(input.value.length, input.value.length);
  }
}

const inputRef = ref<HTMLInputElement>();
watch(model, (value) => {
  if (value.length === 5) {
    emit('filled', value);
    model.value = '';
    inputRef.value?.blur();
  }
});
</script>

<template>
  <label class="code-input" :class="{ focused: isFocused, error: !!error && !isFocused }">
    <input
      ref="inputRef"
      v-model="model"
      textContentType="oneTimeCode"
      type="text"
      inputmode="numeric"
      pattern="[0-9]*"
      maxlength="5"
      class="code-input__input"
      :style="{ left: `${modelLength * 48 + modelLength * 12}px` }"
      @focus="onFocus"
      @blur="onBlur"
      @keydown="moveCursorToEnd"
    >

    <div class="code-input__digits">
      <div class="code-input__digits-item" :class="{ active: activeDigit(0), filled: model[0] }">{{ model[0] }}</div>
      <div class="code-input__digits-item" :class="{ active: activeDigit(1), filled: model[1] }">{{ model[1] }}</div>
      <div class="code-input__digits-item" :class="{ active: activeDigit(2), filled: model[2] }">{{ model[2] }}</div>
      <div class="code-input__digits-item" :class="{ active: activeDigit(3), filled: model[3] }">{{ model[3] }}</div>
      <div class="code-input__digits-item" :class="{ active: activeDigit(4), filled: model[4] }">{{ model[4] }}</div>
    </div>

    <MMText v-if="error" class="code-input__error" font="Body S/Regular" color="negative">
      {{ error }}
    </MMText>
  </label>
</template>

<style lang="stylus" scoped>
.code-input
  display: flex
  flex-direction: column
  align-items: center
  justify-content: flex-start
  position: relative
  width calc(48px * 5 + 12px * 4)
  margin auto
  border-radius: 12px
  gap 12px

  &__input
    width 48px
    height 100%
    border: none
    position: absolute
    top 0
    z-index: 1
    background: transparent
    opacity: 0
    flex 0 0 48px

  &__digits
    display: flex
    gap: 12px
    align-items: center
    justify-content: center
    height 100%
    flex 0 0 48px

    &-item
      position: relative
      display: flex
      align-items: center
      justify-content: center
      background: #F2F4F7
      border: 1px solid inherit
      width 48px
      height 100%
      border-radius: 12px
      cursor: text
      border: 1px solid transparent

      &:not(.filled):after
        content: "•"
        position: absolute
        top: 50%
        left: 50%
        transform: translate(-50%, -50%)
        color: #8B8E99

  &.error
    & .code-input__digits-item
      border: 1px solid #E50000

  &.focused:not(.error)
    & .code-input__digits-item
      &.active
        border: 1px solid #8B8E99

        &:not(.filled):after
          content: "|"
          position: absolute
          top: 50%
          left: 50%
          transform: translate(-50%, -50%)
          width: 100%
          text-align: center
          animation: blink 1.4s infinite
          color: black

      @keyframes blink
        from
          content: "|"
        to
          content: ""
</style>
